/**
 * Activación de la cuenta en la ficha: la barra, la brecha en palabras y los
 * empleados digitales uno por uno, cada uno con su ficha en la plataforma.
 *
 * Sin medición no dibuja una barra en cero: una cuenta que no reporta instalados
 * no es una cuenta con 0% activado, y pintarla así la haría ver peor que a la que
 * de verdad no instaló nada.
 */
import {
  formatMetricValue,
  type Activation,
  type Agente,
  type Links,
} from '@ops/contracts'

import { useVincular } from '../../lib/vincular'
import {
  CLASE_KPI_VALOR,
  ExternalLink,
  Icon,
  ProgressBar,
  SEVERIDAD_LABEL,
  Stack,
  StatusPill,
} from '../../ui'
import { Vacio } from '../comunes'
import { saludActivacion } from './derivados'

export interface FichaActivacionProps {
  nombre: string
  activacion: Activation | null
  agentes: readonly Agente[]
  links: Links['agentes']
}

export function FichaActivacion({
  nombre,
  activacion,
  agentes,
  links,
}: FichaActivacionProps) {
  const vincular = useVincular()
  const salud = saludActivacion(activacion)

  return (
    <Stack gap="sm">
      {activacion === null ? (
        <Vacio punteada
          mensaje="Esta cuenta no reporta instalados todavía."
          detalle="Sin medición no se puede calcular su brecha, así que no entra en el total de la brecha del portfolio."
        />
      ) : (
        <>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            {/* "instalados / contratados" es una razón, no un `MetricValue`:
                va con la clase de `KpiValor` pero sin el componente. */}
            <span className={CLASE_KPI_VALOR}>
              {activacion.instalados} / {activacion.contratados}
            </span>
            <StatusPill severidad={salud} size="sm">
              {formatMetricValue(activacion.pctActivado, 'percent')} ·{' '}
              {SEVERIDAD_LABEL[salud]}
            </StatusPill>
            {activacion.brecha > 0 ? (
              <span className="text-meta font-semibold text-danger-text">
                −{activacion.brecha} empleados contratados sin instalar
              </span>
            ) : (
              <span className="text-meta font-semibold text-accent-text">
                Todo lo contratado está corriendo
              </span>
            )}
          </div>
          <ProgressBar
            pct={activacion.pctActivado}
            label={`Activación de ${nombre}`}
            severidad={salud}
            etiqueta={null}
          />
          <p className="text-micro text-muted-strong">
            Base del porcentaje: {activacion.instalados} instalados sobre{' '}
            {activacion.contratados} contratados.
          </p>
        </>
      )}

      {agentes.length === 0 ? (
        <p className="text-micro text-muted-strong">
          La plataforma no devolvió empleados digitales para esta cuenta.
        </p>
      ) : (
        <ul className="flex list-none flex-col gap-1 p-0">
          {agentes.map((agente) => (
            <li
              key={agente.id}
              data-testid="agente"
              className="flex flex-wrap items-center justify-between gap-2 border-b border-border py-1 last:border-b-0"
            >
              <span className="flex min-w-0 items-center gap-1.5 text-meta text-text">
                <Icon
                  name={agente.instalado ? 'ok' : 'sin-dato'}
                  size={12}
                  className={agente.instalado ? 'text-accent-text' : 'text-muted'}
                />
                <span className="truncate">{agente.nombre}</span>
                <span className="text-micro text-muted-strong">
                  {agente.instalado ? 'corriendo' : 'sin instalar'}
                </span>
              </span>
              <ExternalLink
                link={links[agente.id]?.plataforma ?? null}
                variante="chip"
                sistemaFaltante="la plataforma"
                onVincular={() =>
                  vincular(
                    `el empleado digital «${agente.nombre}» de ${nombre} en la plataforma`,
                  )
                }
              >
                Ver en la plataforma
              </ExternalLink>
            </li>
          ))}
        </ul>
      )}
    </Stack>
  )
}
